import { toast } from "react-toastify";
import { OBJECT_TYPES, GRID_CELL_SIZE_M } from "../../configuratorConstants";
import { getModuleVertices, isPointInModule } from "../../configuratorUtils";

export const handleModuleAction = (state, action, context) => {
  const {
    currentFloorObjects,
    currentFloorId,
    currentFloorIndex,
    t,
    recalculateWallSegmentsAfterRotation,
  } = context;

  switch (action.type) {
    case "UPDATE_MODULE_POSITION": {
      const { id, x, y } = action.payload;
      const nextStateObjects = currentFloorObjects.map((obj) =>
        obj.id === id ? { ...obj, x, y } : obj,
      );
      return {
        handled: true,
        directState: {
          ...state,
          projectObjects: {
            ...state.projectObjects,
            [currentFloorId]: nextStateObjects,
          },
        },
      };
    }

    case "FINALIZE_MODULE_MOVE": {
      const { moduleId, originalX, originalY, originalRotation } =
        action.payload;
      const currentModuleState = currentFloorObjects.find(
        (obj) => obj.id === moduleId,
      );
      if (!currentModuleState) return { handled: true, directState: state };

      const snappedX =
        Math.round(currentModuleState.x / GRID_CELL_SIZE_M) * GRID_CELL_SIZE_M;
      const snappedY =
        Math.round(currentModuleState.y / GRID_CELL_SIZE_M) * GRID_CELL_SIZE_M;

      const currentRotation = currentModuleState.rotation || 0;

      let updatedModule = currentModuleState;
      if (
        originalRotation !== undefined &&
        originalRotation !== currentRotation
      ) {
        updatedModule = recalculateWallSegmentsAfterRotation(
          currentModuleState,
          originalRotation,
          currentRotation,
        );
      }

      const snappedModuleState = {
        ...updatedModule,
        x: snappedX,
        y: snappedY,
      };

      if (currentFloorIndex > 0) {
        const prevFloorId = state.floors[currentFloorIndex - 1].id;
        const ghostModules = (state.projectObjects[prevFloorId] || []).filter(
          (o) => o.type === OBJECT_TYPES.MODULE,
        );
        if (ghostModules.length === 0) {
          setTimeout(() => toast.error(t("toasts.noModulesBelow")), 0);
          return { handled: true, directState: state };
        }
        const vertices = getModuleVertices(snappedModuleState);
        const isSupported = vertices.every((vertex) =>
          ghostModules.some((ghostModule) =>
            isPointInModule(vertex, ghostModule),
          ),
        );
        if (!isSupported) {
          setTimeout(() => toast.error(t("toasts.modulePlacementError")), 0);
          const revertedObjects = currentFloorObjects.map((o) =>
            o.id === moduleId
              ? { ...o, x: originalX, y: originalY, rotation: originalRotation }
              : o,
          );
          return {
            handled: true,
            nextStateObjects: revertedObjects,
            needsRecalculation: true,
            needsValidation: true,
          };
        }
      }

      const nextStateObjects = currentFloorObjects.map((o) =>
        o.id === moduleId ? snappedModuleState : o,
      );

      return {
        handled: true,
        nextStateObjects,
        needsRecalculation: true,
        needsValidation: true,
      };
    }

    case "ROTATE_SELECTED_MODULE": {
      const moduleToRotate = currentFloorObjects.find(
        (obj) =>
          obj.id === state.selectedObjectId && obj.type === OBJECT_TYPES.MODULE,
      );
      if (!moduleToRotate) return { handled: true, directState: state };

      const modulesOnFloor = currentFloorObjects.filter(
        (obj) => obj.type === OBJECT_TYPES.MODULE,
      );
      const firstModule = modulesOnFloor.length > 0 ? modulesOnFloor[0] : null;

      if (!firstModule || firstModule.id !== moduleToRotate.id) {
        setTimeout(() => toast.warn(t("toasts.rotateOnlyFirstModule")), 0);
        return { handled: true, directState: state };
      }

      if (modulesOnFloor.length > 1) {
        setTimeout(
          () => toast.warn(t("toasts.cannotRotateFirstWithOthers")),
          0,
        );
        return { handled: true, directState: state };
      }

      const currentCellsWide = moduleToRotate.cellsWide;
      const currentCellsLong = moduleToRotate.cellsLong;
      const isCurrentlyHorizontal = currentCellsWide > currentCellsLong;

      const fakeOldRotation = isCurrentlyHorizontal ? 90 : 0;
      const fakeNewRotation = isCurrentlyHorizontal ? 0 : 90;

      const rotatedModule = recalculateWallSegmentsAfterRotation(
        moduleToRotate,
        fakeOldRotation,
        fakeNewRotation,
      );

      rotatedModule.rotation = 0;

      if (currentFloorIndex > 0) {
        const prevFloorId = state.floors[currentFloorIndex - 1].id;
        const ghostModules = (state.projectObjects[prevFloorId] || []).filter(
          (o) => o.type === OBJECT_TYPES.MODULE,
        );
        const vertices = getModuleVertices(rotatedModule);
        const isSupported = vertices.every((vertex) =>
          ghostModules.some((ghostModule) =>
            isPointInModule(vertex, ghostModule),
          ),
        );
        if (!isSupported) {
          setTimeout(() => toast.error(t("toasts.moduleRotationError")), 0);
          return { handled: true, directState: state };
        }
      }

      const nextStateObjects = currentFloorObjects.map((obj) =>
        obj.id === rotatedModule.id ? rotatedModule : obj,
      );

      return {
        handled: true,
        nextStateObjects,
        needsRecalculation: true,
        needsValidation: true,
      };
    }

    default:
      return null;
  }
};
